// @ts-nocheck - EXPERIMENTAL: Type issues to be fixed in Phase 3 UI/UX Redesign
// External libraries

// Internal imports
import React, { useEffect, useRef, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDeviceCapabilities } from '@/hooks/use-performance';
import { cn } from '@/lib/utils';

interface MobileOptimizedContainerProps {
  children: React.ReactNode;
  className?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  enablePullToRefresh?: boolean;
  onPullToRefresh?: () => Promise<void>;
  refreshThreshold?: number;
  pullText?: string;
  releaseText?: string;
  loadingText?: string;
}

const pullToRefreshThreshold = 80; // pixels

export function MobileOptimizedContainer({
  children,
  className,
  maxWidth = 'lg',
  padding = 'md',
  enablePullToRefresh = false,
  onPullToRefresh,
  refreshThreshold = pullToRefreshThreshold,
  pullText = 'Pull to refresh',
  releaseText = 'Release to refresh',
  loadingText = 'Refreshing...'
}: MobileOptimizedContainerProps) {
  const isMobile = useIsMobile();
  const capabilities = useDeviceCapabilities();

  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullState, setPullState] = useState<'idle' | 'pulling' | 'releasing' | 'refreshing'>('idle');

  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number>(0);
  const startScrollTop = useRef<number>(0);
  const isScrolling = useRef<boolean>(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!enablePullToRefresh || isRefreshing) return;

    const touch = e.touches[0];
    startY.current = touch.clientY;
    startScrollTop.current = containerRef.current?.scrollTop || 0;
    isScrolling.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!enablePullToRefresh || isRefreshing) return;

    const touch = e.touches[0];
    const currentY = touch.clientY;
    const scrollTop = containerRef.current?.scrollTop || 0;

    // Check if we're at the top and pulling down
    const isAtTop = scrollTop <= 0;
    const isPullingDown = currentY > startY.current;

    if (isAtTop && isPullingDown && !isScrolling.current) {
      const distance = Math.min(currentY - startY.current, refreshThreshold * 1.5);

      if (distance > 10) { // Minimum distance to start showing pull indicator
        setIsPulling(true);
        setPullDistance(distance);

        if (distance >= refreshThreshold) {
          setPullState('releasing');
        } else {
          setPullState('pulling');
        }

        // Apply resistance
        const resistance = distance > refreshThreshold ? 1 + (distance - refreshThreshold) * 0.001 : 1;
        const adjustedDistance = distance * resistance;

        // Update transform for pull indicator
        if (containerRef.current) {
          containerRef.current.style.transform = `translateY(${adjustedDistance}px)`;
        }
      }
    } else {
      // User is scrolling normally
      isScrolling.current = true;
      if (pullState !== 'idle') {
        resetPullToRefresh();
      }
    }
  };

  const handleTouchEnd = async () => {
    if (!enablePullToRefresh || isRefreshing) return;

    if (pullState === 'releasing' && pullDistance >= refreshThreshold) {
      // Trigger refresh
      setIsRefreshing(true);
      setPullState('refreshing');

      // Reset container transform
      if (containerRef.current) {
        containerRef.current.style.transform = 'translateY(0)';
      }

      try {
        await onPullToRefresh?.();
      } catch (error) {
        console.error('Pull to refresh failed:', error);
      } finally {
        setIsRefreshing(false);
        setPullState('idle');
        setPullDistance(0);
      }
    } else {
      // Cancel pull
      resetPullToRefresh();
    }
  };

  const resetPullToRefresh = () => {
    setIsPulling(false);
    setPullDistance(0);
    setPullState('idle');

    if (containerRef.current) {
      containerRef.current.style.transform = 'translateY(0)';
    }
  };

  const getMaxWidthClass = () => {
    switch (maxWidth) {
      case 'sm': return 'max-w-sm';
      case 'md': return 'max-w-md';
      case 'lg': return 'max-w-lg';
      case 'xl': return 'max-w-xl';
      case '2xl': return 'max-w-2xl';
      case 'full': return 'max-w-full';
      default: return 'max-w-lg';
    }
  };

  const getPaddingClass = () => {
    switch (padding) {
      case 'none': return 'p-0';
      case 'sm': return 'p-2 sm:p-4';
      case 'md': return 'p-4 sm:p-6';
      case 'lg': return 'p-6 sm:p-8';
      case 'xl': return 'p-8 sm:p-10';
      default: return 'p-4 sm:p-6';
    }
  };

  const getPullText = () => {
    switch (pullState) {
      case 'pulling': return pullText;
      case 'releasing': return releaseText;
      case 'refreshing': return loadingText;
      default: return '';
    }
  };

  const getPullIcon = () => {
    const iconRotation = Math.min(pullDistance / refreshThreshold, 1) * 180;

    if (pullState === 'refreshing') {
      return (
        <div className="animate-spin">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
      );
    }

    return (
      <div style={{ transform: `rotate(${iconRotation}deg)` }}>
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    );
  };

  return (
    <div className={cn(
      "relative w-full",
      "min-h-screen",
      "transition-all duration-200 ease-out",
      capabilities.prefersReducedMotion && "transition-none",
      className
    )}>
      {/* Pull to refresh indicator */}
      {enablePullToRefresh && (
        <div
          className={cn(
            "absolute top-0 left-0 right-0 z-50",
            "flex items-center justify-center",
            "bg-gradient-to-b from-blue-50 to-transparent dark:from-gray-900",
            "text-blue-600 dark:text-blue-400",
            "transition-all duration-200",
            isPulling || isRefreshing ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          style={{
            height: `${Math.min(pullDistance, refreshThreshold)}px`,
            transform: `translateY(${isPulling || isRefreshing ? 0 : -100}%)`
          }}
        >
          <div className="flex items-center gap-2">
            {getPullIcon()}
            <span className="text-sm font-medium">
              {getPullText()}
            </span>
          </div>
        </div>
      )}

      {/* Main content container */}
      <div
        ref={containerRef}
        className={cn(
          "w-full",
          "mx-auto",
          getMaxWidthClass(),
          getPaddingClass(),
          "transition-transform duration-200 ease-out",
          capabilities.prefersReducedMotion && "transition-none",
          enablePullToRefresh && "overscroll-y-none"
        )}
        style={{
          // Mobile-specific optimizations
          ...(isMobile && {
            WebkitOverflowScrolling: 'touch',
            WebkitTapHighlightColor: 'transparent',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none'
          })
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Safe area inset for mobile devices */}
        <div
          className={cn(
            "relative",
            isMobile && "safe-area-inset-top safe-area-inset-left safe-area-inset-right"
          )}
          style={{
            // Add safe area padding for iOS
            ...(isMobile && {
              paddingTop: 'env(safe-area-inset-top)',
              paddingLeft: 'env(safe-area-inset-left)',
              paddingRight: 'env(safe-area-inset-right)'
            })
          }}
        >
          {children}
        </div>

        {/* Bottom safe area for mobile devices */}
        {isMobile && (
          <div
            className="safe-area-inset-bottom"
            style={{
              height: 'env(safe-area-inset-bottom)',
              minHeight: 'env(safe-area-inset-bottom)'
            }}
          />
        )}
      </div>

      {/* Mobile-specific optimizations */}
      <style jsx>{`
        @media (hover: none) and (pointer: coarse) {
          /* Optimize touch targets for mobile */
          button, [role="button"], a, input, textarea, select {
            min-height: 44px;
            min-width: 44px;
          }
        }

        /* Safe area support for mobile devices */
        .safe-area-inset-top {
          padding-top: env(safe-area-inset-top);
        }
        .safe-area-inset-left {
          padding-left: env(safe-area-inset-left);
        }
        .safe-area-inset-right {
          padding-right: env(safe-area-inset-right);
        }
        .safe-area-inset-bottom {
          padding-bottom: env(safe-area-inset-bottom);
        }

        /* Prevent bounce scroll on iOS */
        ${enablePullToRefresh ? `
          body {
            overflow: hidden;
            position: fixed;
            width: 100%;
            height: 100%;
          }
        ` : ''}

        /* Optimize scrolling performance */
        * {
          -webkit-overflow-scrolling: touch;
        }

        /* Disable text selection on mobile for better UX */
        ${isMobile ? `
          * {
            -webkit-user-select: none;
            -webkit-touch-callout: none;
            -webkit-tap-highlight-color: transparent;
          }

          input, textarea, [contenteditable] {
            -webkit-user-select: auto;
            -webkit-touch-callout: auto;
          }
        ` : ''}
      `}</style>
    </div>
  );
}